// Route → IndexedDB fallback. Each fallback returns EXACTLY the server shape
// (arrays for documents/parts/doctypes, counts object) or null when this
// browser has no usable snapshot, so apiGet rethrows the network error.
import { openOfflineDb, getMeta, toApiDocument } from "./db";
import { getRememberedTenant } from "./envStore";
import { isEncrypted, openRows } from "./secure.js";

async function usableDb() {
  const tenant = getRememberedTenant();
  const db = openOfflineDb(tenant);
  if (!db) return null;
  try {
    const lastSync = await getMeta(db, "lastSync");
    return lastSync ? db : null;
  } catch {
    return null;
  }
}

export const documentsFallback = (partId) => async () => {
  const db = await usableDb();
  if (!db) return null;
  if (isEncrypted()) {
    // displayName is sealed → no index; same code-unit order as Dexie's sortBy.
    const raw = partId ? await db.documents.where("partId").equals(partId).toArray() : await db.documents.toArray();
    const rows = await openRows("documents", raw);
    rows.sort((a, b) => (a.displayName ?? "") < (b.displayName ?? "") ? -1 : (a.displayName ?? "") > (b.displayName ?? "") ? 1 : 0);
    return rows.map(toApiDocument);
  }
  const rows = partId
    ? await db.documents.where("partId").equals(partId).sortBy("displayName")
    : await db.documents.orderBy("displayName").toArray();
  return rows.map(toApiDocument);
};

export const partsFallback = () => async () => {
  const db = await usableDb();
  if (!db) return null;
  const rows = await openRows("parts", await db.parts.toArray());
  rows.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  return rows.map((p) => ({ id: String(p.id), name: p.name, parent_id: p.parent_id || "" }));
};

export const documentCountsFallback = () => async () => {
  const db = await usableDb();
  if (!db) return null;
  const counts = {};
  await db.documents.each((d) => { const pid = d.partId || "1"; counts[pid] = (counts[pid] || 0) + 1; });
  return counts;
};

export const documentTypesFallback = () => async () => {
  const db = await usableDb();
  if (!db) return null;
  return (await openRows("documentTypes", await db.documentTypes.orderBy("order").toArray())).map((t) => t.type);
};

// Step 4 registers the offline search here.
let searchFallbackFactory = null;
export function registerSearchFallback(factory) { searchFallbackFactory = factory; }

export async function resolveFallback(path) {
  let url;
  try { url = new URL(path, "http://offline.local"); } catch { return null; }
  switch (url.pathname) {
    case "/api/documents": return documentsFallback(url.searchParams.get("part_id"));
    case "/api/parts": return partsFallback();
    case "/api/parts/document-counts": return documentCountsFallback();
    case "/api/document-types": return documentTypesFallback();
    case "/api/search-full": return searchFallbackFactory ? searchFallbackFactory(url.searchParams.get("term") || "") : null;
    default: return null;
  }
}
